import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { CheckSquare, Users, ArrowRight, Save, Loader2 } from 'lucide-react';
import { useAuthStore, api } from '../auth/authStore';
import toast from 'react-hot-toast';

interface LeaveApprovalSettings {
  approvalLevels: 'direct_firm_admin' | 'manager_then_firm_admin' | 'manager_only';
}

const LeaveApprovalSettings: React.FC = () => {
  const { user } = useAuthStore();
  const [settings, setSettings] = useState<LeaveApprovalSettings>({
    approvalLevels: 'direct_firm_admin'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Approval level options
  const approvalLevelOptions = [
    {
      value: 'direct_firm_admin',
      label: 'Direct to Firm Admin',
      description: 'Staff applications go directly to Firm Admin for approval',
      workflow: ['Staff', 'Firm Admin'],
      badge: 'Single Level'
    },
    {
      value: 'manager_only',
      label: 'Manager Only',
      description: 'Staff applications are approved by their Manager only',
      workflow: ['Staff', 'Manager'],
      badge: 'Single Level'
    },
    {
      value: 'manager_then_firm_admin',
      label: 'Manager then Firm Admin',
      description: 'Staff applications go to Manager first, then to Firm Admin',
      workflow: ['Staff', 'Manager', 'Firm Admin'],
      badge: 'Two Level'
    }
  ];

  // Load current settings
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/firm-settings');

      if (response.data.success && response.data.data?.attendance?.leavePolicy?.approvalLevels) {
        setSettings({
          approvalLevels: response.data.data.attendance.leavePolicy.approvalLevels
        });
      }
    } catch (error) {
      console.error('Error loading leave approval settings:', error);
      toast.error('Failed to load leave approval settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      const response = await api.put('/firm-settings/leave-approval', settings);

      if (response.data.success) {
        toast.success('Leave approval settings updated successfully');
      } else {
        throw new Error('Failed to update settings');
      }
    } catch (error) {
      console.error('Error saving leave approval settings:', error);
      toast.error('Failed to save leave approval settings');
    } finally {
      setSaving(false);
    }
  };

  const selectedOption = approvalLevelOptions.find(option => option.value === settings.approvalLevels);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        <span>Loading settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Setting Display */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <CheckSquare className="h-5 w-5" />
            <span>Leave Approval Workflow</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">
                Select Approval Workflow
              </label>
              <Select
                value={settings.approvalLevels}
                onValueChange={(value: any) => setSettings({ ...settings, approvalLevels: value })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select approval workflow" />
                </SelectTrigger>
                <SelectContent>
                  {approvalLevelOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center justify-between w-full">
                        <span>{option.label}</span>
                        <Badge variant="secondary" className="ml-2">
                          {option.badge}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Workflow Visualization */}
            {selectedOption && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium mb-2">Current Workflow:</h4>
                <p className="text-sm text-gray-600 mb-3">{selectedOption.description}</p>
                <div className="flex items-center space-x-2">
                  {selectedOption.workflow.map((step, index) => (
                    <React.Fragment key={step}>
                      <div className="flex items-center space-x-2">
                        <Users className="h-4 w-4 text-blue-500" />
                        <span className="text-sm font-medium">{step}</span>
                      </div>
                      {index < selectedOption.workflow.length - 1 && (
                        <ArrowRight className="h-4 w-4 text-gray-400" />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}

            {/* Save Button */}
            <div className="flex justify-end pt-4">
              <Button
                onClick={saveSettings}
                disabled={saving}
                className="flex items-center space-x-2"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                <span>{saving ? 'Saving...' : 'Save Settings'}</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Information Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-3">
            <h4 className="font-medium text-sm">Important Notes:</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Changes will apply to all new leave applications</li>
              <li>• Existing pending applications will continue with their current workflow</li>
              <li>• Both Managers and Firm Admins can approve leaves regardless of the workflow setting</li>
              <li>• Auto-approval settings (if enabled) will override these workflow settings</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LeaveApprovalSettings;
